import React from "react";
import { isForbiddenError } from "../../../app/appUtils";
import PermissionDenied from "../PermissionDenied";

export interface Column<T> {
  key: keyof T | string;
  header: string | React.ReactNode;
  render?: (value: any, item: T) => React.ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyMessage?: string;
  error?: unknown;
}

function Table<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyMessage = "No data available",
  error,
}: TableProps<T>) {
  const renderCell = (item: T, column: Column<T>): React.ReactNode => {
    const value =
      typeof column.key === "string" && column.key.includes(".")
        ? column.key
            .split(".")
            .reduce((obj: any, key: string) => obj?.[key], item)
        : item[column.key as keyof T];

    if (column.render) {
      return column.render(value, item);
    }

    // Convert primitive values to ReactNode
    if (value === null || value === undefined) {
      return "";
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }

    // For complex objects, convert to string representation
    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const isActionColumn = (column: Column<T>) =>
    /^actions?$/i.test(String(column.key));

  const isCheckboxColumn = (column: Column<T>) =>
    String(column.key) === "checkbox";

  const hasLabel = (column: Column<T>) =>
    typeof column.header === "string" && column.header.trim() !== "";

  // Mobile: each row becomes a card. The first data column is the card title,
  // the rest are label/value rows, and actions sit in the card header.
  const renderCards = () => {
    const checkboxColumn = columns.find(isCheckboxColumn);
    const actionColumns = columns.filter(isActionColumn);
    const bodyColumns = columns.filter(
      (column) => !isActionColumn(column) && !isCheckboxColumn(column)
    );
    const [titleColumn, ...detailColumns] = bodyColumns;

    return (
      <div className="md:hidden divide-y divide-[#F1F1F1]">
        {data.map((item, rowIndex) => (
          <div key={rowIndex} className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              {checkboxColumn && (
                <div className="pt-0.5 shrink-0">
                  {renderCell(item, checkboxColumn)}
                </div>
              )}
              <div className="min-w-0 flex-1 text-sm font-semibold text-hColor break-words [&_img]:shrink-0 [&_svg]:shrink-0">
                {titleColumn ? renderCell(item, titleColumn) : null}
              </div>
              {actionColumns.length > 0 && (
                <div className="flex shrink-0 items-center gap-2">
                  {actionColumns.map((column, index) => (
                    <div key={`${String(column.key)}-${index}`}>
                      {renderCell(item, column)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {detailColumns.length > 0 && (
              <dl className="space-y-2">
                {detailColumns.map((column, colIndex) => (
                  <div
                    key={`${rowIndex}-${String(column.key)}-${colIndex}`}
                    className="flex items-start justify-between gap-4 text-sm"
                  >
                    {hasLabel(column) && (
                      <dt className="shrink-0 text-[12px] font-medium text-pColor">
                        {column.header}
                      </dt>
                    )}
                    <dd
                      className={`min-w-0 break-words text-hColor ${
                        hasLabel(column) ? "text-right" : "flex-1"
                      }`}
                    >
                      {renderCell(item, column)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSkeleton = () => (
    <div className="bg-white rounded-[10px] border border-gray-200 overflow-hidden">
      <div className="md:hidden divide-y divide-[#F1F1F1]">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={`skeleton-card-${index}`} className="p-4 space-y-3">
            <div className="h-4 w-2/3 bg-gray-300 rounded animate-pulse"></div>
            <div className="h-3 w-full bg-gray-200 rounded animate-pulse"></div>
            <div className="h-3 w-5/6 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary/30">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={`skeleton-header-${index}`}
                  className="px-6 py-4 text-left text-[16px] font-semibold text-hColor tracking-wider"
                >
                  <div className="h-4 bg-gray-300 rounded animate-pulse"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#F1F1F1]">
            {Array.from({ length: 5 }).map((_, rowIndex) => (
              <tr key={`skeleton-row-${rowIndex}`}>
                {columns.map((_, colIndex) => (
                  <td
                    key={`skeleton-cell-${rowIndex}-${colIndex}`}
                    className="px-6 py-4 whitespace-nowrap text-sm"
                  >
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // The user is signed in but lacks permission for this resource (403)
  if (!loading && data.length === 0 && isForbiddenError(error)) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <PermissionDenied />
      </div>
    );
  }

  // Show skeleton only when loading AND no data is available
  if (loading && data.length === 0) {
    return renderSkeleton();
  }

  // Show empty state when not loading and no data
  if (!loading && data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-12 text-center">
          <p className="text-hColor">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Show data table (regardless of loading state if data exists)
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden relative">
      {/* Optional loading indicator overlay when updating existing data */}
      {loading && data.length > 0 && (
        <div className="absolute top-2 right-2 z-10">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
        </div>
      )}

      {renderCards()}

      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary/30">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={`${String(column.key)}-${index}`}
                  style={{ width: column.width }}
                  className="px-6 py-4 text-left text-[13px] font-semibold text-hColor tracking-wider"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#F1F1F1]">
            {data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={`${rowIndex}-${String(column.key)}-${colIndex}`}
                    className={`
                      px-6 py-4 whitespace-nowrap text-sm
                      ${column.align === "center" ? "text-center" : ""}
                      ${column.align === "right" ? "text-right" : ""}
                    `}
                  >
                    {renderCell(item, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Table;

// ${column.width ? `w-${column.width}` : ""}
//                     ${column.align === "center" ? "text-center" : ""}
//                     ${column.align === "right" ? "text-right" : ""}
